import { cellOf, type CapMap } from './capacity';
import { monthKeyOf, currentMonthKey } from './months';
import type { ReleaseCal } from './releases';

// Realismus-Check: Bei gleichmäßiger Verteilung von Ressourcen und Aufwänden
// über die Monate — können wir das Restpensum mit der Restkapa schaffen?
export type RealismStatus = 'green' | 'green-light' | 'yellow' | 'red' | 'done' | 'no-data';

export type HealthKey =
  | 'unknown' | 'completed' | 'expiredOpen' | 'pastDue'
  | 'noCapacity' | 'breeze' | 'doable' | 'tight' | 'unrealistic';

export type RealismCheck = {
  status: RealismStatus;
  emoji: string;
  healthKey: HealthKey;

  totalKapa: number;
  pastKapa: number;        // Monate vor dem aktuellen
  remainingKapa: number;   // aktueller Monat anteilig + zukünftige Monate
  monthsTotal: number;
  monthsPast: number;
  monthsRemaining: number; // = monthsTotal − monthsPast (current zählt anteilig zu beidem)
  currentMonthProgress: number; // 0..1

  // Arbeit
  totalPlan: number;       // Detail (offen + done)
  doneIst: number;         // Ist (done-Teilmenge)
  remainingWork: number;   // totalPlan − doneIst

  // Verhältnisse
  utilizationRemaining: number | null;  // remainingWork / remainingKapa
  utilizationTotal: number | null;       // totalPlan / totalKapa

  // Soll-Ist-Tempo
  expectedDone: number;        // anteilig zum verstrichenen Zeitraum
  paceDelta: number;           // doneIst − expectedDone (positiv = voraus)
};

function monthsBetween(startISO: string, endISO: string): string[] {
  const s = new Date(startISO);
  const e = new Date(endISO);
  if (isNaN(s.getTime()) || isNaN(e.getTime())) return [];
  const out: string[] = [];
  const cur = new Date(s.getFullYear(), s.getMonth(), 1);
  const last = new Date(e.getFullYear(), e.getMonth(), 1);
  while (cur <= last) {
    out.push(monthKeyOf(cur));
    cur.setMonth(cur.getMonth() + 1);
  }
  return out;
}

// Anteil des aktuellen Monats, der schon vorbei ist (0..1). Sinnvoll
// für eine genauere Soll-Ist-Rechnung: am 15. eines 30-Tage-Monats
// sind 50 % verstrichen.
function currentMonthProgress(now: Date): number {
  const last = new Date(now.getFullYear(), now.getMonth() + 1, 0).getDate();
  return Math.min(1, now.getDate() / last);
}

function sumKapaForMonth(
  cap: CapMap,
  release: string,
  monthKey: string,
  team?: string
): number {
  if (team) {
    return cellOf(cap[team]?.[release]?.[monthKey]);
  }
  let total = 0;
  for (const teamCap of Object.values(cap)) {
    total += cellOf(teamCap?.[release]?.[monthKey]);
  }
  return total;
}

export function computeRealism(opts: {
  release: string;
  releaseCal: Record<string, ReleaseCal>;
  capacityByTeam: CapMap;
  team?: string;
  totalPlan: number;
  doneIst: number;
}): RealismCheck {
  const { release, releaseCal, capacityByTeam, team, totalPlan, doneIst } = opts;
  const cal = releaseCal[release];

  const empty: RealismCheck = {
    status: 'no-data',
    emoji: '·',
    healthKey: 'unknown',
    totalKapa: 0, pastKapa: 0, remainingKapa: 0,
    monthsTotal: 0, monthsPast: 0, monthsRemaining: 0,
    currentMonthProgress: 0,
    totalPlan, doneIst,
    remainingWork: Math.max(0, totalPlan - doneIst),
    utilizationRemaining: null, utilizationTotal: null,
    expectedDone: 0, paceDelta: 0,
  };
  if (!cal?.start || !cal?.end) return empty;

  const months = monthsBetween(cal.start, cal.end);
  if (!months.length) return empty;

  const now = new Date();
  const curKey = currentMonthKey();
  const progress = currentMonthProgress(now);

  let totalKapa = 0, pastKapa = 0, remainingKapa = 0;
  let monthsPast = 0;
  for (const mk of months) {
    const k = sumKapaForMonth(capacityByTeam, release, mk, team);
    totalKapa += k;
    if (mk < curKey) {
      pastKapa += k;
      monthsPast += 1;
    } else if (mk === curKey) {
      // aktueller Monat anteilig
      pastKapa     += k * progress;
      remainingKapa += k * (1 - progress);
      monthsPast    += progress;
    } else {
      remainingKapa += k;
    }
  }
  const monthsTotal = months.length;
  const monthsRemaining = monthsTotal - monthsPast;

  const remainingWork = Math.max(0, totalPlan - doneIst);
  const utilizationRemaining = remainingKapa > 0 ? remainingWork / remainingKapa : null;
  const utilizationTotal     = totalKapa     > 0 ? totalPlan     / totalKapa     : null;
  const expectedDone = totalPlan * (monthsPast / monthsTotal);
  const paceDelta    = doneIst - expectedDone;

  let status: RealismStatus;
  let emoji: string;
  let healthKey: HealthKey;

  if (monthsRemaining <= 0 || remainingKapa <= 0) {
    if (remainingWork <= 0) {
      status = 'done'; emoji = '🎉'; healthKey = 'completed';
    } else if (totalKapa > 0 && utilizationTotal !== null && utilizationTotal <= 1) {
      status = 'red'; emoji = '🛑'; healthKey = 'expiredOpen';
    } else {
      status = 'red'; emoji = '🛑'; healthKey = 'pastDue';
    }
  } else if (totalKapa <= 0) {
    status = 'no-data'; emoji = '·'; healthKey = 'noCapacity';
  } else if (utilizationRemaining === null) {
    return { ...empty, totalKapa, pastKapa, remainingKapa, monthsTotal, monthsPast, monthsRemaining, currentMonthProgress: progress, remainingWork };
  } else if (utilizationRemaining <= 0.70) {
    status = 'green'; emoji = '🟢'; healthKey = 'breeze';
  } else if (utilizationRemaining <= 0.85) {
    status = 'green-light'; emoji = '✅'; healthKey = 'doable';
  } else if (utilizationRemaining <= 1.0) {
    status = 'yellow'; emoji = '⚠️'; healthKey = 'tight';
  } else {
    status = 'red'; emoji = '🛑'; healthKey = 'unrealistic';
  }

  return {
    status, emoji, healthKey,
    totalKapa, pastKapa, remainingKapa,
    monthsTotal, monthsPast, monthsRemaining,
    currentMonthProgress: progress,
    totalPlan, doneIst, remainingWork,
    utilizationRemaining, utilizationTotal,
    expectedDone, paceDelta,
  };
}
